import React, {useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {MISSING_ID} from '@common/utils'

import {CalculableDisabled, ComponentValue} from './types'

import {StyledDashboardInnerCardTableCell} from '~/components/facility/GeneralDataCard.styled'
import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Button} from '~/components/general/button/Button'
import {IconButton} from '~/components/general/iconButton/IconButton'
import {Icon} from '~/components/general/icon/Icon'
import {useGetPatientAppointmentFile} from '~/api'

const styles = {
  tableCell: {
    width: '50%',
  },
}

export type NamedFileInputProps = {
  title: string
  name: string
  fileName?: string
  fileId?: string
  pendingFile?: File
  patientId: string
  appointmentId: string
  onRemove: () => void
  onFileSelect: (file: File) => void
  disabled?: CalculableDisabled
  currentData: ComponentValue[]
  required?: boolean
}

export const NamedFileInput: React.FC<NamedFileInputProps> = ({
  title,
  name,
  fileName,
  fileId,
  pendingFile,
  patientId,
  appointmentId,
  onRemove,
  onFileSelect,
  disabled = false,
  currentData,
  required,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const {t} = useTranslation<string>()
  const {data: fileUrlData, isFetching: isFileUrlLoading} =
    useGetPatientAppointmentFile(
      {
        fileId: fileId ?? MISSING_ID,
        patientId,
        appointmentId,
      },
      {enabled: Boolean(fileId)},
    )

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      onFileSelect(file)
    }
    event.target.value = ''
  }

  const handleChooseFile = () => {
    fileInputRef.current?.click()
  }

  const isDisabled =
    typeof disabled === 'function' ? disabled(currentData) : disabled

  return (
    <StyledDashboardInnerCardTableCell style={styles.tableCell}>
      <Container flex direction="column">
        <Typography weight="medium" size="sm" required={required} inline>
          {title}
        </Typography>
        {fileId ? (
          <Container flex direction="row" spacing={1} align="center">
            {isFileUrlLoading ? (
              <Typography size="sm" color="grey">
                {t('Loading...')}
              </Typography>
            ) : fileUrlData?.url ? (
              <Container flex direction="row" spacing={1} align="center">
                <a
                  href={fileUrlData.url}
                  target="_blank"
                  rel="noopener noreferrer">
                  <Typography size="sm" color="primary">
                    {fileName ?? name ?? ''}
                  </Typography>
                </a>
                <Icon name="Share" size="xs" color="primary" />
              </Container>
            ) : (
              <Typography size="sm" color="grey">
                {fileName ?? name ?? ''}
              </Typography>
            )}
            <IconButton
              name="Remove"
              onClick={onRemove}
              disabled={isDisabled}
              color="secondary"
            />
          </Container>
        ) : (
          <Container flex direction="row" spacing={1} align="center">
            <input
              ref={fileInputRef}
              type="file"
              style={{display: 'none'}}
              onChange={handleFileChange}
              disabled={isDisabled}
            />
            {pendingFile ? (
              <Container flex direction="row" spacing={1} align="center">
                <Typography size="sm">{pendingFile.name}</Typography>
                <IconButton
                  name="Remove"
                  onClick={onRemove}
                  disabled={isDisabled}
                  color="secondary"
                />
              </Container>
            ) : (
              <Container flex direction="row" spacing={1} align="center">
                <Button
                  onClick={handleChooseFile}
                  disabled={isDisabled}
                  size="xs">
                  {t('Choose File')}
                </Button>
              </Container>
            )}
          </Container>
        )}
      </Container>
    </StyledDashboardInnerCardTableCell>
  )
}
